import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiGet, apiPost, apiUpload } from '../../services/api'
import { useOnlineStatus } from '../../hooks/useOnlineStatus'
import { saveOfflineSubmission, saveOfflineAttachment } from '../../services/offline'
import {
  ArrowLeft,
  Camera,
  Image,
  FileText,
  Upload,
  CheckCircle,
  AlertCircle,
  Users,
  Calendar,
  Clock,
  Trash2,
} from 'lucide-react'
import LoadingSpinner from '../../components/common/LoadingSpinner'

interface Indicator {
  id: number
  code: string
  name: string
  target: number
  max_points: number
  points_per_unit: number
}

interface Period {
  id: number
  year: number
  month: number
  name?: string
  is_locked: boolean
}

interface Submission {
  id: number
  submission_date: string
  status: string
  notes?: string
  created_at: string
}

export default function MoMSubmissionPage() {
  const navigate = useNavigate()
  const isOnline = useOnlineStatus()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const cameraInputRef = useRef<HTMLInputElement>(null)

  const [indicator, setIndicator] = useState<Indicator | null>(null)
  const [periods, setPeriods] = useState<Period[]>([])
  const [selectedPeriod, setSelectedPeriod] = useState<Period | null>(null)
  const [submissions, setSubmissions] = useState<Submission[]>([])
  const [submissionCount, setSubmissionCount] = useState(0)
  const [calculatedPoints, setCalculatedPoints] = useState(0)

  const [file, setFile] = useState<File | null>(null)
  const [filePreview, setFilePreview] = useState<string | null>(null)
  const [notes, setNotes] = useState('')
  const [meetingDate, setMeetingDate] = useState(new Date().toISOString().split('T')[0])

  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [showUploadOptions, setShowUploadOptions] = useState(false)

  useEffect(() => {
    fetchData()
  }, [])

  useEffect(() => {
    if (selectedPeriod && indicator) {
      fetchSubmissions()
    }
  }, [selectedPeriod, indicator])

  const fetchData = async () => {
    try {
      // Fetch C.1 indicator
      const indicators = await apiGet<Indicator[]>('/kpi/indicators', { is_active: true })
      const momIndicator = indicators.find(i => i.code === 'C.1')
      if (momIndicator) {
        setIndicator(momIndicator)
      }

      // Fetch periods
      const periodsData = await apiGet<Period[]>('/kpi/periods')
      const activePeriods = periodsData.filter(p => !p.is_locked)
      setPeriods(activePeriods)

      if (activePeriods.length > 0) {
        setSelectedPeriod(activePeriods[0])
      }
    } catch (err) {
      console.error('Failed to load data:', err)
      setError('Failed to load data. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchSubmissions = async () => {
    if (!selectedPeriod?.id || !indicator?.id) return

    try {
      // Fetch scores
      try {
        const scoresData = await apiGet<{
          scores: Array<{ indicator: Indicator; submission_count: number; calculated_score: number }>
        }>('/kpi/scores', { period_id: selectedPeriod.id })

        const momScore = scoresData.scores.find(s => s.indicator.code === 'C.1')
        if (momScore) {
          setSubmissionCount(momScore.submission_count)
          setCalculatedPoints(momScore.calculated_score)
        }
      } catch (scoreErr) {
        console.error('Failed to load scores:', scoreErr)
      }

      // Fetch recent submissions
      const submissionsData = await apiGet<{ items: Submission[] }>('/kpi/submissions', {
        period_id: selectedPeriod.id,
        indicator_id: indicator.id,
        per_page: 10,
      })
      setSubmissions(submissionsData.items || [])
    } catch (err) {
      console.error('Failed to load submissions:', err)
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (selectedFile) {
      if (selectedFile.size > 16 * 1024 * 1024) {
        setError('File size must be less than 16MB')
        return
      }

      setFile(selectedFile)
      setError(null)
      setShowUploadOptions(false)

      // Create preview for images
      if (selectedFile.type.startsWith('image/')) {
        const reader = new FileReader()
        reader.onloadend = () => {
          setFilePreview(reader.result as string)
        }
        reader.readAsDataURL(selectedFile)
      } else {
        setFilePreview(null)
      }
    }
  }

  const handleCameraCapture = () => {
    cameraInputRef.current?.click()
    setShowUploadOptions(false)
  }

  const handleGallerySelect = () => {
    fileInputRef.current?.click()
    setShowUploadOptions(false)
  }

  const removeFile = () => {
    setFile(null)
    setFilePreview(null)
  }

  const handleSubmit = async () => {
    if (!selectedPeriod || !indicator) {
      setError('Please select a period')
      return
    }

    if (!file) {
      setError('Please upload the Minutes of Meeting file')
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      if (isOnline) {
        // Online submission
        const response = await apiPost<{ id: number }>('/kpi/submissions', {
          period_id: selectedPeriod.id,
          indicator_id: indicator.id,
          submission_date: meetingDate,
          value: 1,
          notes: notes || undefined,
        })

        // Upload attachment
        if (response.id) {
          const formData = new FormData()
          formData.append('file', file)
          formData.append('submission_id', String(response.id))
          await apiUpload('/kpi/attachments', formData)
        }
      } else {
        // Offline submission
        const clientId = await saveOfflineSubmission({
          client_id: `mom_${Date.now()}`,
          period_id: selectedPeriod.id,
          indicator_id: indicator.id,
          submission_date: meetingDate,
          value: 1,
          notes: notes || undefined,
          created_at: new Date().toISOString(),
        })

        await saveOfflineAttachment(clientId, file, file.name, file.type)
      }

      setSuccess(true)

      // Reset form
      setTimeout(() => {
        setFile(null)
        setFilePreview(null)
        setNotes('')
        setMeetingDate(new Date().toISOString().split('T')[0])
        setSuccess(false)
        fetchSubmissions()
      }, 2000)
    } catch (err) {
      console.error('Submission failed:', err)
      setError('Failed to submit. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <div className="mobile-page flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  const progress = indicator ? Math.min((submissionCount / indicator.target) * 100, 100) : 0

  return (
    <div className="mobile-page bg-gray-50">
      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept="image/*,.pdf,.doc,.docx"
        onChange={handleFileSelect}
      />
      <input
        ref={cameraInputRef}
        type="file"
        className="hidden"
        accept="image/*"
        capture="environment"
        onChange={handleFileSelect}
      />

      {/* Header */}
      <div className="bg-blue-500 px-4 pt-4 pb-4">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => navigate('/kpi')} className="mobile-back-btn">
            <ArrowLeft className="h-6 w-6 text-white" />
          </button>
          <div className="flex-1">
            <h1 className="text-white text-lg font-semibold">Minutes of Meeting</h1>
            <p className="text-white/80 text-xs">C.1 - Periodic Meeting with Customer</p>
          </div>
        </div>

        {/* Progress Card */}
        <div className="bg-white rounded-xl shadow-lg p-4">
          <div className="flex items-center gap-4">
            <div className="bg-blue-100 rounded-xl p-3">
              <Users className="h-8 w-8 text-blue-500" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold text-gray-900">
                  {submissionCount} / {indicator?.target || 15}
                </span>
                <span className="text-sm font-semibold text-blue-500">
                  {calculatedPoints.toFixed(2)} pts
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Max {indicator?.max_points || 10} points
              </p>
              <div className="mt-2 bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-500 rounded-full h-2 transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Form Section */}
      <div className="px-4 mt-4 pb-32">
        {/* Period Selection */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
          <label className="text-sm font-medium text-gray-700 mb-2 block">
            Select Period
          </label>
          <select
            value={selectedPeriod?.id || ''}
            onChange={(e) => {
              const period = periods.find(p => p.id === Number(e.target.value))
              setSelectedPeriod(period || null)
            }}
            className="mobile-select"
          >
            {periods.map((period) => (
              <option key={period.id} value={period.id}>
                {period.name || `${period.month}/${period.year}`}
              </option>
            ))}
          </select>
        </div>

        {/* Meeting Date */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
          <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Meeting Date
          </label>
          <input
            type="date"
            value={meetingDate}
            onChange={(e) => setMeetingDate(e.target.value)}
            className="mobile-input"
          />
        </div>

        {/* File Upload */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
          <label className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Minutes of Meeting File
            <span className="text-red-500">*</span>
          </label>

          {!file ? (
            <button
              onClick={() => setShowUploadOptions(true)}
              className="mobile-upload-area"
            >
              <Upload className="h-10 w-10 text-gray-400 mb-2" />
              <span className="text-gray-600 font-medium">Tap to upload MoM</span>
              <span className="text-gray-400 text-xs mt-1">
                Image, PDF, or Document (max 16MB)
              </span>
            </button>
          ) : (
            <div className="mobile-file-preview">
              {filePreview ? (
                <img src={filePreview} alt="Preview" className="mobile-file-image" />
              ) : (
                <div className="mobile-file-icon">
                  <FileText className="h-12 w-12 text-blue-500" />
                </div>
              )}
              <div className="mobile-file-info">
                <p className="font-medium text-gray-900 truncate">{file.name}</p>
                <p className="text-xs text-gray-500">
                  {(file.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
              <button onClick={removeFile} className="mobile-file-remove">
                <Trash2 className="h-5 w-5" />
              </button>
            </div>
          )}
        </div>

        {/* Notes */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
          <label className="text-sm font-medium text-gray-700 mb-2 block">
            Notes (Optional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add meeting notes or description..."
            rows={3}
            className="mobile-textarea"
          />
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4 flex items-center gap-3">
            <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
            <p className="text-sm text-green-700">
              {isOnline ? 'MoM submitted successfully!' : 'Saved offline. Will sync when online.'}
            </p>
          </div>
        )}

        {/* Offline Warning */}
        {!isOnline && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-4 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-yellow-500 flex-shrink-0" />
            <p className="text-sm text-yellow-700">
              You're offline. Submission will be saved locally and synced later.
            </p>
          </div>
        )}

        {/* Recent Submissions */}
        {submissions.length > 0 && (
          <div className="mt-6">
            <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Recent Submissions
            </h3>
            <div className="space-y-2">
              {submissions.slice(0, 5).map((sub) => (
                <div key={sub.id} className="bg-white rounded-xl p-3 shadow-sm flex items-center gap-3">
                  <div className="bg-blue-100 rounded-lg p-2">
                    <FileText className="h-5 w-5 text-blue-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">
                      {new Date(sub.submission_date).toLocaleDateString('en-US', {
                        weekday: 'short',
                        day: 'numeric',
                        month: 'short',
                      })}
                    </p>
                    {sub.notes && (
                      <p className="text-xs text-gray-500 truncate">{sub.notes}</p>
                    )}
                  </div>
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                    sub.status === 'approved' ? 'bg-green-100 text-green-700' :
                    sub.status === 'rejected' ? 'bg-red-100 text-red-700' :
                    'bg-blue-100 text-blue-700'
                  }`}>
                    {sub.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Submit Button - Fixed at bottom */}
      <div className="mobile-submit-container">
        <button
          onClick={handleSubmit}
          disabled={isSubmitting || !file}
          className="mobile-submit-btn"
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <div className="spinner border-white" />
              Submitting...
            </span>
          ) : (
            'Submit MoM'
          )}
        </button>
      </div>

      {/* Upload Options Modal */}
      {showUploadOptions && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-50"
            onClick={() => setShowUploadOptions(false)}
          />
          <div className="mobile-upload-modal">
            <div className="text-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Upload MoM File</h3>
              <p className="text-sm text-gray-500">Choose upload method</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button onClick={handleCameraCapture} className="mobile-upload-option">
                <Camera className="h-8 w-8 text-blue-500 mb-2" />
                <span className="text-sm font-medium">Camera</span>
              </button>
              <button onClick={handleGallerySelect} className="mobile-upload-option">
                <Image className="h-8 w-8 text-green-500 mb-2" />
                <span className="text-sm font-medium">Gallery</span>
              </button>
            </div>
            <button
              onClick={() => setShowUploadOptions(false)}
              className="w-full mt-4 py-3 text-gray-600 font-medium"
            >
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  )
}
